import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import {
  DeliveryLog,
  DeliveryLogState,
  EligibleBatch,
  IncompleteDeliveryGroup,
  BulkCreateData,
  DeliveryCompletionData,
} from '../../types/deliveryLog';
import deliveryLogApiService from '../../services/deliveryLogApiService';

// Helper function to create a blank delivery log with default values
export const createBlankDeliveryLog = (): DeliveryLog => {
  const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
  const currentTime = new Date().toTimeString().slice(0, 5); // HH:MM

  return {
    id: uuidv4(),
    deliveryGroupId: null,
    deliveryDate: today,
    deliveryTime: currentTime,
    fillingId: '',
    fillingName: '',
    batchRecordId: '',
    batchLotNumber: '',
    quantity: 0,
    customerDestination: '',
    transportationTemp: null,
    temperatureUnit: 'F',
    initials: null,
    correctiveAction: '',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
};

// Async Thunks

export const fetchDeliveryLogs = createAsyncThunk(
  'deliveryLogs/fetchDeliveryLogs',
  async () => {
    return await deliveryLogApiService.fetchDeliveryLogs();
  }
);

export const createDeliveryLogAsync = createAsyncThunk(
  'deliveryLogs/createDeliveryLog',
  async (deliveryLog: DeliveryLog) => {
    return await deliveryLogApiService.createDeliveryLog(deliveryLog);
  }
);

export const updateDeliveryLogAsync = createAsyncThunk(
  'deliveryLogs/updateDeliveryLog',
  async (deliveryLog: DeliveryLog) => {
    return await deliveryLogApiService.updateDeliveryLog(deliveryLog);
  }
);

export const deleteDeliveryLogAsync = createAsyncThunk(
  'deliveryLogs/deleteDeliveryLog',
  async (id: string) => {
    await deliveryLogApiService.deleteDeliveryLog(id);
    return id;
  }
);

// New Async Thunks for Delivery Wizard

export const fetchEligibleBatchesAsync = createAsyncThunk(
  'deliveryLogs/fetchEligibleBatches',
  async (fillingId?: string) => {
    return await deliveryLogApiService.fetchEligibleBatches(fillingId);
  }
);

export const bulkCreateDeliveryLogsAsync = createAsyncThunk(
  'deliveryLogs/bulkCreate',
  async (data: BulkCreateData) => {
    return await deliveryLogApiService.bulkCreateDeliveryLogs(data);
  }
);

export const fetchIncompleteDeliveryGroupsAsync = createAsyncThunk(
  'deliveryLogs/fetchIncompleteGroups',
  async () => {
    return await deliveryLogApiService.fetchIncompleteDeliveryGroups();
  }
);

export const completeDeliveryGroupAsync = createAsyncThunk(
  'deliveryLogs/completeGroup',
  async ({ groupId, data }: { groupId: string; data: DeliveryCompletionData }) => {
    return await deliveryLogApiService.completeDeliveryGroup(groupId, data);
  }
);

// Initial State
const initialState: DeliveryLogState = {
  logs: [],
  incompleteDeliveryGroups: [],
  eligibleBatches: [],
  loading: false,
  error: null,
};

// Slice
const deliveryLogsSlice = createSlice({
  name: 'deliveryLogs',
  initialState,
  reducers: {
    // Synchronous reducers (if needed in the future)
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Delivery Logs
    builder
      .addCase(fetchDeliveryLogs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDeliveryLogs.fulfilled, (state, action: PayloadAction<DeliveryLog[]>) => {
        state.loading = false;
        state.logs = action.payload;
      })
      .addCase(fetchDeliveryLogs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch delivery logs';
      });

    // Create Delivery Log
    builder
      .addCase(createDeliveryLogAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createDeliveryLogAsync.fulfilled, (state, action: PayloadAction<DeliveryLog>) => {
        state.loading = false;
        state.logs.push(action.payload);
      })
      .addCase(createDeliveryLogAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create delivery log';
      });

    // Update Delivery Log
    builder
      .addCase(updateDeliveryLogAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateDeliveryLogAsync.fulfilled, (state, action: PayloadAction<DeliveryLog>) => {
        state.loading = false;
        const index = state.logs.findIndex((log) => log.id === action.payload.id);
        if (index !== -1) {
          state.logs[index] = action.payload;
        }
      })
      .addCase(updateDeliveryLogAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update delivery log';
      });

    // Delete Delivery Log
    builder
      .addCase(deleteDeliveryLogAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteDeliveryLogAsync.fulfilled, (state, action: PayloadAction<string>) => {
        state.loading = false;
        state.logs = state.logs.filter((log) => log.id !== action.payload);
      })
      .addCase(deleteDeliveryLogAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete delivery log';
      });

    // Fetch Eligible Batches
    builder
      .addCase(fetchEligibleBatchesAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEligibleBatchesAsync.fulfilled, (state, action: PayloadAction<EligibleBatch[]>) => {
        state.loading = false;
        state.eligibleBatches = action.payload;
      })
      .addCase(fetchEligibleBatchesAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch eligible batches';
      });

    // Bulk Create Delivery Logs
    builder
      .addCase(bulkCreateDeliveryLogsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkCreateDeliveryLogsAsync.fulfilled, (state, action: PayloadAction<DeliveryLog[]>) => {
        state.loading = false;
        state.logs.push(...action.payload);
      })
      .addCase(bulkCreateDeliveryLogsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to bulk create delivery logs';
      });

    // Fetch Incomplete Delivery Groups
    builder
      .addCase(fetchIncompleteDeliveryGroupsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchIncompleteDeliveryGroupsAsync.fulfilled, (state, action: PayloadAction<IncompleteDeliveryGroup[]>) => {
        state.loading = false;
        state.incompleteDeliveryGroups = action.payload;
      })
      .addCase(fetchIncompleteDeliveryGroupsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch incomplete delivery groups';
      });

    // Complete Delivery Group
    builder
      .addCase(completeDeliveryGroupAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(completeDeliveryGroupAsync.fulfilled, (state, action: PayloadAction<DeliveryLog[]>) => {
        state.loading = false;
        // Update logs with the completed data
        for (const updatedLog of action.payload) {
          const index = state.logs.findIndex((log) => log.id === updatedLog.id);
          if (index !== -1) {
            state.logs[index] = updatedLog;
          }
        }
        // Remove the completed group from incompleteDeliveryGroups
        if (action.payload.length > 0) {
          const completedGroupId = action.payload[0].deliveryGroupId;
          state.incompleteDeliveryGroups = state.incompleteDeliveryGroups.filter(
            (group) => group.deliveryGroupId !== completedGroupId
          );
        }
      })
      .addCase(completeDeliveryGroupAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to complete delivery group';
      });
  },
});

export const { clearError } = deliveryLogsSlice.actions;
export default deliveryLogsSlice.reducer;
